/**
 * mp4box-probe.js — extract container metadata from mp4/mov/m4v files
 * without spinning up FFmpeg WASM.
 *
 * Why this exists:
 *   FFmpeg WASM init takes 5-15 s on low-RAM devices; for mp4-family
 *   files we don't actually need a full decoder to extract Duration /
 *   width / height / hasAudio — the moov atom holds all of it. mp4box.js
 *   is ~160 KB and reads byte-streams to surface this info in <100 ms.
 *
 *   Integrated by video-trim (and to be reused by other video tools)
 *   so the workspace opens *instantly* for mp4 users instead of waiting
 *   for FFmpeg to load. FFmpeg WASM is still used for the actual trim
 *   pipeline because `-c copy` works regardless of the codec — but it
 *   can be loaded lazily only when the user clicks Save.
 *
 *   For non-mp4 containers (avi/mkv/ts) probe falls back through; the
 *   tool's existing FFmpeg path handles those.
 *
 * Usage:
 *   const info = await window.__Mp4BoxProbe(file);
 *   // → { duration, width, height, hasAudio, codec, container }
 *   // throws if file isn't a parsable mp4/mov, or moov atom isn't
 *   // reachable within the first 32 MB (rare; usually it's in the
 *   // first 1 MB after `-faststart` remux).
 */
(function(){
'use strict';
// Skip only if the REAL impl is already installed; a stub from
// timbrica-core.js __sharedFunction must be replaced (it's just a queue).
if (window.__Mp4BoxProbe && !window.__Mp4BoxProbe.__isStub) return;

var libLoadPromise = null;
function loadMp4Box() {
    if (window.MP4Box) return Promise.resolve();
    if (libLoadPromise) return libLoadPromise;
    libLoadPromise = new Promise(function(resolve, reject){
        var s = document.createElement('script');
        s.src = '/vendor/mp4box/mp4box.all.min.js';
        s.onload = function(){ resolve(); };
        s.onerror = function(){
            // Fallback chain: Beget cross-domain (works for RKN-blocked RU users
            // and saves NL egress for intl) → jsdelivr LAST. Version pinned to
            // the vendored 0.5.2 (download-all.sh).
            var s2 = document.createElement('script');
            s2.src = 'https://timbrica.com/vendor/mp4box/mp4box.all.min.js';
            s2.onload = function(){ resolve(); };
            s2.onerror = function(){
                var s3 = document.createElement('script');
                s3.src = 'https://cdn.jsdelivr.net/npm/mp4box@0.5.2/dist/mp4box.all.min.js';
                s3.onload = function(){ resolve(); };
                s3.onerror = function(){ reject(new Error('mp4box.js failed to load')); };
                document.head.appendChild(s3);
            };
            document.head.appendChild(s2);
        };
        document.head.appendChild(s);
    });
    return libLoadPromise;
}

var CHUNK_BYTES = 4 * 1024 * 1024;        // 4 MB at a time
/* Bytes-read budget — caps the *actual* number of bytes pulled from disk, not
   the *offset* mp4box can seek to. mp4box knows the box structure: when it
   sees mdat headers it asks to skip directly to the next box (typically moov
   at end-of-file), so the bytes-actually-read for moov-at-end is just the
   ftyp + a few atoms + moov ≈ a few MB, regardless of file size. The previous
   offset cap broke this — a 1 GB unstreamed mp4 with moov-at-end was rejected
   even though mp4box only needed ~5 MB of reads. Budget kicks in only if
   mp4box actually wants linear bytes that exceed the budget (rare — corrupt
   file, or a parser pathology). */
var BYTES_READ_BUDGET = 64 * 1024 * 1024;

// Exported so other tools can get the LIBRARY without running a probe.
// /video-filter's datamosh needs the demuxer, not the verdict, and the
// three-tier fallback chain above (local → Beget cross-domain → jsdelivr) is
// the part nobody should copy: it is what keeps RU users behind blocks working.
window.__loadMp4Box = loadMp4Box;

window.__Mp4BoxProbe = async function(file) {
    if (!file || typeof file.slice !== 'function') {
        throw new Error('Mp4BoxProbe: argument is not a Blob/File');
    }
    await loadMp4Box();

    return await new Promise(function(resolve, reject){
        var mp4box = window.MP4Box.createFile();
        var settled = false;
        var timer = setTimeout(function(){
            if (settled) return;
            settled = true;
            try { mp4box.flush(); } catch(_){}
            reject(new Error('mp4box probe timeout (>10 s)'));
        }, 10000);

        mp4box.onReady = function(info) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            var video = (info.tracks || []).find(function(t){ return t.type === 'video'; });
            /* mp4box.js classifies QuickTime PCM (codec 'twos', 'sowt', 'in24',
               '23ni', 'in32', 'fl32', 'lpcm', 'raw', 'ulaw', 'alaw') as
               type='metadata' because the QT sample description box doesn't
               match its audio handler. The track still has the PCM FourCC in
               t.codec — so we also accept any non-video track whose codec
               matches a known QT PCM FourCC. Otherwise MOV files from Apple
               devices / Pro Tools report hasAudio=false even when the file
               clearly carries audio (confirmed 2026-05-28: sid 7d5692e2
               MOV/pcm_s24le hit this path on prod). */
            var _qtPcm = /^(twos|sowt|in24|23ni|in32|23in|fl32|fl64|lpcm|raw ?|ulaw|alaw)$/i;
            var audio = (info.tracks || []).find(function(t){
                if (t.type === 'audio') return true;
                if (t.type === 'video') return false;
                return _qtPcm.test(String(t.codec || '').trim());
            });
            /* «Has a sound track» is the track's HANDLER ('soun'), not the type
               mp4box derives from a sample entry it happens to know. Apple's
               spatial-audio track (`apac`, iPhone 16/17) is 'soun' too, and
               mp4box files it as metadata — so the FourCC list above could
               only ever lag behind the next codec. A 'soun' track we cannot
               decode is still sound: saying «no audio» about it is what turns
               «we could not read it» into «the file is silent». */
            var soundTrak = false;
            try {
                var _traks = (mp4box.moov && mp4box.moov.traks) || [];
                for (var _i = 0; _i < _traks.length && !soundTrak; _i++) {
                    var _h = _traks[_i] && _traks[_i].mdia && _traks[_i].mdia.hdlr;
                    if (_h && _h.handler === 'soun') soundTrak = true;
                }
            } catch (_) {}
            resolve({
                duration: info.timescale ? info.duration / info.timescale : 0,
                width: video && video.video ? video.video.width : (video && video.track_width) || 0,
                height: video && video.video ? video.video.height : (video && video.track_height) || 0,
                hasAudio: !!audio || soundTrak,
                codec: video ? video.codec : '',
                container: 'mp4',
                tracks: info.tracks || [],
            });
        };
        mp4box.onError = function(err) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            reject(new Error('mp4box parse error: ' + (err || 'unknown')));
        };

        var offset = 0;
        var bytesRead = 0;

        async function feed() {
            try {
                while (!settled && offset < file.size) {
                    var end = Math.min(offset + CHUNK_BYTES, file.size);
                    var slice = file.slice(offset, end);
                    var buf = await slice.arrayBuffer();
                    bytesRead += buf.byteLength;
                    if (bytesRead > BYTES_READ_BUDGET) {
                        // Reading too much linearly — likely a malformed file or
                        // mp4box pathology. moov-at-end normally costs <= 5 MB
                        // of reads; needing >64 MB means something else is wrong.
                        if (settled) return;
                        settled = true;
                        clearTimeout(timer);
                        reject(new Error('mp4box read >' + BYTES_READ_BUDGET + ' bytes without finding moov'));
                        return;
                    }
                    buf.fileStart = offset;
                    // appendBuffer returns the next file offset mp4box wants. If it
                    // returns the same offset twice, we've fed enough for that range
                    // — break out and let onReady fire from synchronous handler.
                    var next = mp4box.appendBuffer(buf);
                    if (settled) return;
                    if (next <= offset) {
                        // mp4box doesn't need anything before `next` — skip ahead.
                        // If next === offset it's stuck waiting for more bytes after
                        // our current end, just continue from end.
                        offset = Math.max(end, next);
                    } else {
                        // Explicit seek — mp4box has parsed enough atom headers to
                        // know what to skip (typically a multi-GB mdat). The skip
                        // costs zero bytes-read; we resume reading from `next`.
                        offset = next;
                    }
                }
                if (!settled) {
                    // Read up to file end without onReady firing — try a final
                    // flush in case moov is at the very end.
                    try { mp4box.flush(); } catch(_){}
                    if (!settled) {
                        settled = true;
                        clearTimeout(timer);
                        reject(new Error('mp4box parsed entire file (' + bytesRead + ' bytes read) without finding moov'));
                    }
                }
            } catch (e) {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                reject(e);
            }
        }
        feed();
    });
};

})();

// Drain stub queue installed by timbrica-core.js __sharedFunction
// (defer-onerror race defense — see helper comment for context).
if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__Mp4BoxProbe');
